import { useState, useEffect } from 'react';
import firebase from 'firebase/compat/app';
import { auth } from '../services/firebase';
import { setupUserDataListener } from '../utils/firebase';
import { UserData } from '../types';

/**
 * useAuth hook
 * Manages authentication state and user data
 * React Router handles navigation, so this hook no longer manages view state
 */
export const useAuth = () => {
  const [userData, setUserData] = useState<UserData>({
    userAddress: null,
    rentAmount: null,
    rentPaid: null,
    dueDate: null,
    isAdmin: null
  });
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<firebase.User | null>(null);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user && user.emailVerified) {
        setCurrentUser(user);
        // Keep loading true until userData is populated
        // This will be set to false after userData listener sets up
      } else if (!user) {
        setCurrentUser(null);
        // Reset user data when logged out
        setUserData({
          userAddress: null,
          rentAmount: null,
          rentPaid: null,
          dueDate: null,
          isAdmin: null
        });
        setIsLoading(false);
      } else {
        // User exists but email not verified
        setCurrentUser(null);
        setIsLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // Listen for user data changes when user is authenticated
  useEffect(() => {
    if (currentUser) {
      const cleanup = setupUserDataListener(currentUser, (newUserData) => {
        setUserData(newUserData);
        // Once we have user data, we can stop loading
        setIsLoading(false);
      });
      return cleanup;
    }
  }, [currentUser]);

  return {
    userData,
    isLoading,
    currentUser
  };
};
